/**
 * QuoteEditor - Core component for creating and editing quotes and templates
 *
 * Single component that handles all quote/template editing scenarios:
 * - Create new quote
 * - Edit existing quote
 * - Create new template
 * - Edit existing template
 * - Create quote from template
 *
 * Architecture:
 * - Uses react-hook-form for state management
 * - Composed of focused section components
 * - Actions passed as children for flexibility
 * - Parent controls all state (controlled component)
 */

import { useTranslation } from "react-i18next";
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import { useOrganization } from "@clerk/clerk-react";
import { useTaxCalculation } from "@/hooks/use-tax-calculation";
import { useFormPricing } from "@/hooks/use-pricing";
import { useServices } from "@/hooks/use-services";
import { useCustomParts } from "@/hooks/use-custom-parts";
import { useProductsLookup } from "@/hooks/use-products";
import useOrg from "@/hooks/use-org";
import { PricingService } from "@shared/pricing-service";
import { quoteToPricingInput } from "@shared/pricing-utils";
import { centsToDollars } from "@/lib/price-utils";
import { insertQuoteSchema, insertQuoteTemplateSchema } from "@shared/schema";
import type {
  QuoteService,
  QuoteProduct,
  QuoteCustomPart,
  Quote,
  Customer,
} from "@onsemble/shared";
import type { Product } from "@/types/product";
import { EditorLayout } from "./EditorLayout";
import { QuoteViewer } from "../QuoteViewer";
import { QuoteInfoSection } from "./sections/QuoteInfoSection";
import { LaborSection } from "./sections/LaborSection";
import { ServicesSection } from "./sections/ServicesSection";
import { PartsSection } from "./sections/PartsSection";
import { PricingSection } from "./sections/PricingSection";
import { NotesSection } from "./sections/NotesSection";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { type InsertCustomer } from "@shared/schema";
import { PricingOptions } from "@/components/quotes/PricingOptions";
import { useCreateCustomer } from "@/requests/customers/create-customer";

// Extend shared schemas for frontend-specific fields
const quoteFormSchema = insertQuoteSchema.extend({
  templateId: z.string().optional(),
  // quoteServices is already in insertQuoteSchema, no need to add it here
});

const templateFormSchema = insertQuoteTemplateSchema.extend({
  // quoteServices is already in insertQuoteTemplateSchema if it exists
});

export type QuoteFormData = z.infer<typeof quoteFormSchema>;
export type TemplateFormData = z.infer<typeof templateFormSchema>;

export interface QuoteEditorProps {
  mode: "create" | "edit";
  entityType: "quote" | "template";
  initialData?: Partial<QuoteFormData> | Partial<TemplateFormData>;
  onSave: (data: QuoteFormData | TemplateFormData) => void | Promise<void>;
  onCancel?: () => void;
  onSaveAsTemplate?: (formData: QuoteFormData) => void;
  isSubmitting?: boolean;

  // Optional data for creating quotes from templates
  templateId?: string;

  // Customer and product data
  customers?: Customer[];
  templates?: Array<{
    uid: string;
    name: string;
    description?: string;
    laborHours?: number;
    laborRate?: number;
    markupPercent?: number;
    partsAffectPrice?: boolean;
    discountPercentage?: number;
    discountDescription?: string;
    defaultNotes?: string;
    products?: QuoteProduct[];
    quoteServices?: QuoteService[];
    quoteCustomParts?: QuoteCustomPart[];
  }>;
  products?: Product[];
  productsLoading?: boolean;
  productsError?: Error | null;

  className?: string;
}

/**
 * QuoteEditor Component
 *
 * Usage:
 * ```tsx
 * // Create new quote
 * <QuoteEditor
 *   mode="create"
 *   entityType="quote"
 *   onSave={handleSave}
 *   customers={customers}
 * />
 *
 * // Edit existing quote
 * <QuoteEditor
 *   mode="edit"
 *   entityType="quote"
 *   initialData={quote}
 *   onSave={handleSave}
 *   customers={customers}
 * />
 *
 * // Create template
 * <QuoteEditor
 *   mode="create"
 *   entityType="template"
 *   onSave={handleSave}
 * />
 * ```
 */
export const QuoteEditor: React.FC<QuoteEditorProps> = ({
  mode,
  entityType,
  initialData,
  onSave,
  onCancel,
  onSaveAsTemplate,
  isSubmitting = false,
  templateId,
  customers = [],
  templates = [],
  products = [],
  productsLoading = false,
  productsError: _productsError = null,
  className = "",
}) => {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const { toast } = useToast();
  const { calculateTax } = useTaxCalculation();
  const {
    services,
    isLoading: servicesLoading,
    error: servicesError,
  } = useServices();
  const {
    customParts,
    isLoading: customPartsLoading,
    error: customPartsError,
  } = useCustomParts();
  const { org } = useOrg();
  const { mutate: createCustomer, isPending: isCreatingCustomer } =
    useCreateCustomer();

  // Determine which schema to use based on entity type
  const schema = entityType === "quote" ? quoteFormSchema : templateFormSchema;

  // Initialize form with react-hook-form
  const form = useForm<QuoteFormData | TemplateFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      // Common fields
      title: "",
      description: "",
      laborHours: 0,
      laborRate: 0,
      markupPercent: 0,
      partsAffectPrice: true,
      discountPercentage: 0,
      discountDescription: "",
      notes: "",
      products: [],
      quoteServices: [],
      quoteCustomParts: [],

      // Calculated fields - initialized to 0, will be calculated on submission
      partsCostPremarkup: 0,
      partsCostPostmarkup: 0,
      amountPrediscount: 0,
      amountPresalesTax: 0,
      amountPostsalesTax: 0,
      taxAmount: 0,

      // Quote-specific fields
      ...(entityType === "quote" && {
        customerId: "",
        quoteStatus: "ready_to_send" as const,
        approvalCode: "",
        customerApprovalDate: null,
      }),

      // Template-specific fields
      ...(entityType === "template" && {
        name: "",
        organizationId: organization?.id || "",
      }),

      // Merge with initial data
      ...initialData,

      // Template ID if creating from template
      ...(templateId && { templateId }),
    },
  });

  // Reset form when initialData changes (e.g., when template loads)
  // This is needed because defaultValues only applies on mount, but initialData
  // may be undefined initially and then populated when templates load
  useEffect(() => {
    const defaultFormValues = {
      // Common fields
      title: "",
      description: "",
      laborHours: 0,
      laborRate: 0,
      markupPercent: 0,
      partsAffectPrice: true,
      discountPercentage: 0,
      discountDescription: "",
      notes: "",
      products: [],
      quoteServices: [],
      quoteCustomParts: [],

      // Calculated fields
      partsCostPremarkup: 0,
      partsCostPostmarkup: 0,
      amountPrediscount: 0,
      amountPresalesTax: 0,
      amountPostsalesTax: 0,
      taxAmount: 0,

      // Quote-specific fields
      ...(entityType === "quote" && {
        customerId: "",
        quoteStatus: "ready_to_send" as const,
        approvalCode: "",
        customerApprovalDate: null,
      }),

      // Template-specific fields
      ...(entityType === "template" && {
        name: "",
        organizationId: organization?.id || "",
      }),

      // Merge with initial data if it exists
      ...(initialData || {}),

      // Template ID if creating from template
      ...(templateId && { templateId }),
    };

    form.reset(defaultFormValues);
  }, [initialData, templateId, entityType, organization?.id, form]);

  // Dialog state management
  const [dialogs, setDialogs] = useState({
    preview: false,
    saveTemplate: false,
    customerCreate: false,
    templateChange: false,
  });

  // Template selection state
  const [previousTemplateId, setPreviousTemplateId] = useState<string | null>(
    null,
  );
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(
    null,
  );

  // Extract product IDs from form data for preview product lookup
  // Only extracts when preview dialog is open to avoid unnecessary re-renders
  const previewProductIds = useMemo(() => {
    if (!dialogs.preview) return [];
    const formData = form.getValues();
    if ("products" in formData && formData.products) {
      return formData.products
        .map((p: QuoteProduct) => p.productId)
        .filter(Boolean);
    }
    return [];
  }, [dialogs.preview, form]);

  // Fetch products specifically for preview display
  // This ensures preview always has access to the actual selected products,
  // not just the initial 50 products from the catalog
  const {
    products: previewProducts,
    isLoading: previewProductsLoading,
    error: previewProductsError,
  } = useProductsLookup(
    previewProductIds,
    previewProductIds.length > 0 && dialogs.preview,
  );

  // Handle customer creation
  const handleCreateCustomer = (data: InsertCustomer) => {
    createCustomer(data, {
      onSuccess: (newCustomer) => {
        setDialogs((d) => ({ ...d, customerCreate: false }));
        // Automatically select the newly created customer
        form.setValue("customerId" as any, newCustomer.uid, {
          shouldValidate: true,
          shouldDirty: true,
        });
        form.clearErrors("customerId" as any);
        toast({
          title: t("features.quotes.quoteEditor.index.customerCreatedSuccessfully"),
        });
      },
      onError: (error) => {
        toastApiError(
          toast,
          error,
          t("features.quotes.quoteEditor.index.failedToCreateCustomer"),
        );
      },
    });
  };

  // Template population logic
  const populateFromTemplate = useCallback(
    (templateId: string, skipConfirmation: boolean = false) => {
      // Check if this is a change that needs confirmation
      const isChange =
        previousTemplateId !== null &&
        previousTemplateId !== templateId &&
        previousTemplateId !== "";

      if (isChange && !skipConfirmation) {
        // Store pending template and show confirmation dialog
        setPendingTemplateId(templateId);
        setDialogs((d) => ({ ...d, templateChange: true }));
        return;
      }

      // Handle "no template" selection
      if (templateId === "" || templateId === "no-template") {
        form.reset({
          ...form.getValues(),
          title: "",
          description: "",
          notes: "",
          laborHours: 0,
          laborRate: 0,
          markupPercent: 0,
          partsAffectPrice: true,
          discountPercentage: 0,
          discountDescription: "",
          quoteServices: [],
          quoteCustomParts: [],
          products: [],
        });
        setPreviousTemplateId(templateId);
        return;
      }

      // Find and apply the selected template
      const selectedTemplate = templates?.find(
        (template) => template.uid === templateId,
      );

      if (selectedTemplate) {
        // Populate form with template data
        form.setValue("description", selectedTemplate.description || "");
        form.setValue("notes", (selectedTemplate as any).defaultNotes || "");
        form.setValue("laborHours", selectedTemplate.laborHours ?? 0);
        form.setValue("laborRate", selectedTemplate.laborRate || 0);
        form.setValue("markupPercent", selectedTemplate.markupPercent ?? 0);
        form.setValue(
          "partsAffectPrice",
          selectedTemplate.partsAffectPrice ?? true,
        );
        form.setValue(
          "discountPercentage",
          selectedTemplate.discountPercentage ?? 0,
        );
        form.setValue(
          "discountDescription",
          selectedTemplate.discountDescription ?? "",
        );

        // Handle services - use quoteServices directly (matching custom parts pattern)
        if (
          selectedTemplate.quoteServices &&
          Array.isArray(selectedTemplate.quoteServices) &&
          selectedTemplate.quoteServices.length > 0
        ) {
          form.setValue("quoteServices", selectedTemplate.quoteServices);
        } else {
          form.setValue("quoteServices", []);
        }

        // Handle products
        if (
          selectedTemplate.products &&
          Array.isArray(selectedTemplate.products) &&
          selectedTemplate.products.length > 0
        ) {
          form.setValue("products", selectedTemplate.products);
        } else {
          form.setValue("products", []);
        }

        // Handle custom parts
        if (
          selectedTemplate.quoteCustomParts &&
          Array.isArray(selectedTemplate.quoteCustomParts) &&
          selectedTemplate.quoteCustomParts.length > 0
        ) {
          form.setValue("quoteCustomParts", selectedTemplate.quoteCustomParts);
        } else {
          form.setValue("quoteCustomParts", []);
        }

        setPreviousTemplateId(templateId);
      }
    },
    [previousTemplateId, templates, form],
  );

  // Handle template selection change
  const handleTemplateChange = (value: string) => {
    const templateId = value === "no-template" ? "" : value;
    form.setValue("templateId" as any, templateId);
    populateFromTemplate(templateId);
  };

  // Confirm template change dialog handlers
  const handleConfirmTemplateChange = () => {
    if (pendingTemplateId) {
      populateFromTemplate(pendingTemplateId, true);
      form.setValue("templateId" as any, pendingTemplateId);
      setPreviousTemplateId(pendingTemplateId);
    }
    setDialogs((d) => ({ ...d, templateChange: false }));
    setPendingTemplateId(null);
  };

  const handleCancelTemplateChange = () => {
    // Revert the form selection
    form.setValue("templateId" as any, previousTemplateId || "");
    setDialogs((d) => ({ ...d, templateChange: false }));
    setPendingTemplateId(null);
  };

  const getPendingTemplateName = () => {
    if (pendingTemplateId === "" || pendingTemplateId === "no-template") {
      return "No template";
    }
    const template = templates?.find((t) => t.uid === pendingTemplateId);
    return template ? template.name : "the selected template";
  };

  // Fetch specific selected products (handles products not in initial catalog load)
  // This ensures pricing calculations have access to ALL selected products
  const selectedProductIds = useMemo(
    () => (form.watch("products") || []).map((sp) => sp.productId),
    [form.watch("products")],
  );

  const { products: fetchedProducts, isLoading: isFetchingProducts } =
    useProductsLookup(selectedProductIds);

  // Merge catalog products with fetched products for complete product data
  const allProducts = useMemo(() => {
    const productsMap = new Map<string, Product>();

    // Add products from props (initial catalog load)
    (products || []).forEach((p) => productsMap.set(p.uid, p));

    // Add/override with fetched products (specific selected products)
    fetchedProducts.forEach((p) => productsMap.set(p.uid, p));

    return Array.from(productsMap.values());
  }, [products, fetchedProducts]);

  // Real-time pricing calculations
  const quoteServices = (form.watch("quoteServices") || []) as QuoteService[];
  const quoteCustomParts = (form.watch("quoteCustomParts") ||
    []) as QuoteCustomPart[];

  const pricingHook = useFormPricing({
    laborHours: form.watch("laborHours"),
    laborRate: form.watch("laborRate"),
    products: form.watch("products") || [],
    productsData: allProducts, // Use merged products instead of just catalog
    markupPercent: form.watch("markupPercent"),
    partsAffectPrice: form.watch("partsAffectPrice"),
    services: services,
    quoteServices: quoteServices,
    customParts: customParts,
    quoteCustomParts: quoteCustomParts,
    discountPercentage: form.watch("discountPercentage"),
    discountDescription: form.watch("discountDescription"),
  });

  // Handle form submission
  const handleSubmit = async (data: QuoteFormData | TemplateFormData) => {
    console.log("Form submitted with data:", data);

    // For templates, submit directly without tax calculation
    if (entityType === "template") {
      try {
        await onSave(data);
      } catch (error) {
        console.error("Template save error:", error);
        toast({
          title: t("features.quotes.quoteEditor.index.saveFailed"),
          description: t("features.quotes.quoteEditor.index.failedToSaveTemplate"),
          variant: "destructive",
        });
      }
      return;
    }

    // For quotes, calculate tax and pricing before submitting
    const quoteData = data as QuoteFormData;

    // Get the selected customer
    const selectedCustomer = customers?.find(
      (c) => c.uid === quoteData.customerId,
    );

    // Validate required data for tax calculation
    if (
      !org?.address?.street ||
      !org?.address?.city ||
      !org?.address?.state ||
      !org?.address?.zipCode
    ) {
      toast({
        title: t("features.quotes.quoteEditor.index.companyAddressRequired"),
        description:
          t("features.quotes.quoteEditor.index.pleaseAddYourCompany"),
        variant: "destructive",
      });
      return;
    }

    if (!selectedCustomer) {
      toast({
        title: t("features.quotes.quoteEditor.index.customerRequired"),
        description: t("features.quotes.quoteEditor.index.pleaseSelectACustomer"),
        variant: "destructive",
      });
      return;
    }

    if (!selectedCustomer.zipCode || !selectedCustomer.state) {
      toast({
        title: t("features.quotes.quoteEditor.index.customerAddressRequired"),
        description:
          t("features.quotes.quoteEditor.index.pleaseCompleteTheCustomer"),
        variant: "destructive",
      });
      return;
    }

    // Use quoteToPricingInput to convert quoteServices to PricingInput format
    const pricingInput = quoteToPricingInput(
      quoteData,
      allProducts,
      services ?? [],
      customParts ?? [],
    );

    const pricingResult = PricingService.calculatePricing(pricingInput);

    // Extract pricing values with explicit type safety (all in cents)
    const totalBeforeDiscount: number = pricingResult.subtotalBeforeDiscount;
    const preSalesTaxAmount: number = pricingResult.subtotalAfterDiscount;
    const partsCostPremarkup: number = pricingResult.partsCostPremarkup;
    const partsCostPostmarkup: number = pricingResult.partsCostPostmarkup;

    let salesTaxAmount: number = 0;

    // Calculate tax only if there's an amount to tax
    if (preSalesTaxAmount > 0) {
      try {
        const result = await calculateTax({
          customerZip: selectedCustomer.zipCode,
          customerState: selectedCustomer.state,
          customerCountry: selectedCustomer.country || "US",
          amount: centsToDollars(preSalesTaxAmount),
          servicesLaborTotal: centsToDollars(pricingResult.servicesLaborTotal),
          partsTotal: centsToDollars(pricingResult.partsCost),
        });

        if (
          result.success &&
          result.taxAmount !== null &&
          typeof result.taxAmount === "number"
        ) {
          salesTaxAmount = result.taxAmount; // Tax amount in cents
        } else {
          console.error("Tax calculation failed:", result.error);
          toast({
            title: t("features.quotes.quoteEditor.index.taxCalculationFailed"),
            description:
              t("features.quotes.quoteEditor.index.unableToCalculateSales_2"),
            variant: "destructive",
          });
          return;
        }
      } catch (error) {
        console.error("Tax calculation failed:", error);
        toast({
          title: t("features.quotes.quoteEditor.index.taxCalculationError"),
          description:
            t("features.quotes.quoteEditor.index.unableToCalculateSales"),
          variant: "destructive",
        });
        return;
      }
    }

    const finalAmount: number = preSalesTaxAmount + salesTaxAmount;

    // Update data with calculated values - ensure all numeric fields are properly typed
    const updatedData: QuoteFormData = {
      ...quoteData,
      laborRate: quoteData.laborRate ?? 0,
      partsCostPremarkup,
      partsCostPostmarkup,
      markupPercent: quoteData.markupPercent ?? 0,
      partsAffectPrice: quoteData.partsAffectPrice ?? true,
      amountPrediscount: totalBeforeDiscount,
      amountPresalesTax: preSalesTaxAmount,
      amountPostsalesTax: finalAmount,
      taxAmount: salesTaxAmount,
    };

    try {
      await onSave(updatedData);
    } catch (error) {
      console.error("Quote save error:", error);
      toast({
        title: t("features.quotes.quoteEditor.index.saveFailed"),
        description: t("features.quotes.quoteEditor.index.failedToSaveQuote"),
        variant: "destructive",
      });
    }
  };

  // Preview data for dialog
  const getPreviewData = (): Partial<Quote> => {
    const formData = form.getValues();

    // Extract only the fields that are part of Quote type
    // quoteServices is already in the right format (matching custom parts pattern)
    // Handle both QuoteFormData and TemplateFormData types
    const baseData = formData as QuoteFormData | TemplateFormData;

    // Get quoteServices and quoteCustomParts - they should exist on both types now
    const quoteServices =
      "quoteServices" in baseData && baseData.quoteServices
        ? (baseData.quoteServices as QuoteService[])
        : [];
    const quoteCustomParts =
      "quoteCustomParts" in baseData && baseData.quoteCustomParts
        ? (baseData.quoteCustomParts as QuoteCustomPart[])
        : [];

    return {
      customerId: "customerId" in baseData ? baseData.customerId : undefined,
      title: "title" in baseData ? baseData.title || "" : "",
      description: baseData.description || "",
      notes: "notes" in baseData ? baseData.notes : undefined,
      laborHours: baseData.laborHours,
      laborRate: baseData.laborRate,
      markupPercent: baseData.markupPercent,
      partsCostPremarkup: baseData.partsCostPremarkup,
      partsCostPostmarkup: baseData.partsCostPostmarkup,
      partsAffectPrice: baseData.partsAffectPrice,
      discountPercentage: baseData.discountPercentage,
      discountDescription: baseData.discountDescription,
      quoteServices: quoteServices,
      quoteCustomParts: quoteCustomParts,
      products: ("products" in baseData
        ? baseData.products
        : []) as QuoteProduct[],
      // Add pricing calculations for preview
      amountPrediscount: pricingHook.subtotalBeforeDiscount,
      amountPresalesTax: pricingHook.subtotalAfterDiscount,
      amountPostsalesTax: pricingHook.finalTotal,
      taxAmount: 0, // Preview doesn't calculate real tax
    };
  };

  // Render header
  const renderHeader = () => {
    const title =
      mode === "create"
        ? `Create New ${entityType === "quote" ? "Quote" : "Template"}`
        : `Edit ${entityType === "quote" ? "Quote" : "Template"}`;

    return (
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <h1 className="text-xl sm:text-2xl font-semibold">{title}</h1>

          {/* Template selector with save button - only show for create mode and quotes */}
          {mode === "create" && entityType === "quote" && (
            <div className="flex items-center gap-2">
              <Label
                htmlFor="template-select"
                className="text-sm font-medium whitespace-nowrap"
              >
                {t("features.quotes.quoteEditor.index.template")}
              </Label>
              <Select
                value={form.watch("templateId" as any) || "no-template"}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger id="template-select" className="w-full sm:w-48">
                  <SelectValue placeholder={t("features.quotes.quoteEditor.index.selectTemplateOptional")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no-template">{t("features.quotes.quoteEditor.index.noTemplate")}</SelectItem>
                  {templates && templates.length > 0 ? (
                    templates.map((template) => (
                      <SelectItem key={template.uid} value={template.uid}>
                        {template.name}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-templates-available" disabled>
                      {t("features.quotes.quoteEditor.index.noTemplatesAvailable")}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {onSaveAsTemplate && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onSaveAsTemplate(form.getValues() as QuoteFormData)
                  }
                  disabled={isSubmitting}
                  className="whitespace-nowrap"
                >
                  {t("features.quotes.quoteEditor.index.saveAsTemplate")}
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {entityType === "quote" && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogs((d) => ({ ...d, preview: true }))}
            >
              {t("features.quotes.quoteEditor.index.preview")}
            </Button>
          )}
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button
            type="submit"
            form="quote-editor-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? t("common.saving") : t("features.quotes.quoteEditor.index.save")}
          </Button>
        </div>
      </div>
    );
  };

  // Render sidebar with pricing options and summary
  const renderSidebar = () => {
    return (
      <div className="space-y-4">
        {/* Pricing Options - Desktop only */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-3">{t("features.quotes.quoteEditor.index.pricingOptions")}</h3>
          <PricingOptions
            markupPercent={form.watch("markupPercent") ?? 0}
            onMarkupPercentChange={(value) =>
              form.setValue("markupPercent", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            discountPercentage={form.watch("discountPercentage") ?? 0}
            onDiscountPercentageChange={(value) =>
              form.setValue("discountPercentage", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            discountDescription={form.watch("discountDescription") ?? ""}
            onDiscountDescriptionChange={(value) =>
              form.setValue("discountDescription", value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            totalAmount={centsToDollars(pricingHook.subtotalBeforeDiscount)}
            className="bg-transparent p-0"
          />
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h3 className="text-lg font-medium mb-3">{t("features.quotes.quoteEditor.index.summary")}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("features.quotes.quoteEditor.index.labor")}</span>
              <span>
                ${centsToDollars(pricingHook.pricing.laborCost).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("features.quotes.quoteEditor.index.services")}</span>
              <span>
                ${centsToDollars(pricingHook.pricing.servicesCost).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("features.quotes.quoteEditor.index.parts")}</span>
              <span>
                ${centsToDollars(pricingHook.pricing.partsCost).toFixed(2)}
              </span>
            </div>
            {pricingHook.discountAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>{t("features.quotes.quoteEditor.index.discount")}</span>
                <span>
                  -${centsToDollars(pricingHook.discountAmount).toFixed(2)}
                </span>
              </div>
            )}
            <div className="pt-2 border-t">
              <div className="flex justify-between font-semibold">
                <span>{t("features.quotes.quoteEditor.index.subtotal")}</span>
                <span>
                  $
                  {centsToDollars(pricingHook.subtotalAfterDiscount).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Form {...form}>
        <form
          id="quote-editor-form"
          onSubmit={form.handleSubmit(handleSubmit, (errors) => {
            console.error("Form validation errors:", errors);
            toast({
              title: t("features.quotes.quoteEditor.index.validationError"),
              description: t("features.quotes.quoteEditor.index.pleaseCheckAllRequired"),
              variant: "destructive",
            });

            // Scroll to first error
            const firstErrorField = Object.keys(errors)[0];
            if (firstErrorField) {
              const fieldElement = document.querySelector(
                `[name="${firstErrorField}"]`,
              );
              if (fieldElement) {
                fieldElement.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
                (fieldElement as HTMLElement).focus();
              }
            }
          })}
          className={className}
        >
          <EditorLayout header={renderHeader()} sidebar={renderSidebar()}>
            <div className="space-y-6">
              {/* Quote/Template Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <QuoteInfoSection
                  form={form}
                  entityType={entityType}
                  customers={customers}
                  onCreateCustomer={() =>
                    setDialogs((d) => ({ ...d, customerCreate: true }))
                  }
                />

                <LaborSection form={form} />
              </div>

              {/* Services */}
              <ServicesSection
                form={form}
                organizationId={organization?.id || ""}
              />

              {/* Parts & Materials */}
              <PartsSection
                form={form}
                organizationId={organization?.id || ""}
                products={allProducts}
                productsLoading={productsLoading || isFetchingProducts}
                isSubmitting={isSubmitting}
              />

              {/* Pricing Options (mobile/tablet only) */}
              <PricingSection
                form={form}
                subtotalBeforeDiscount={pricingHook.subtotalBeforeDiscount}
              />

              {/* Notes */}
              <NotesSection form={form} />
            </div>
          </EditorLayout>
        </form>
      </Form>

      {/* Preview Dialog */}
      <Dialog
        open={dialogs.preview}
        onOpenChange={(open) => setDialogs((d) => ({ ...d, preview: open }))}
      >
        <DialogContent
          className="sm:max-w-[900px] max-h-[90vh] p-0 flex flex-col"
          hideCloseButton
        >
          <div className="flex-1 overflow-y-auto p-6">
            <QuoteViewer
              data={getPreviewData()}
              viewMode="preview"
              products={previewProducts}
              services={services || []}
              customParts={customParts || []}
              productsLoading={previewProductsLoading}
              servicesLoading={servicesLoading}
              customPartsLoading={customPartsLoading}
              productsError={previewProductsError}
              servicesError={servicesError as Error | null}
              customPartsError={customPartsError as Error | null}
              hidePartDetails={true}
            />
          </div>
          <div className="border-t bg-background px-6 py-4 flex justify-end">
            <Button
              onClick={() => setDialogs((d) => ({ ...d, preview: false }))}
              className="min-w-[120px]"
            >
              {t("common.close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Creation Dialog */}
      <Dialog
        open={dialogs.customerCreate}
        onOpenChange={(open) => {
          if (!open) {
            // When dialog closes, reset the customer selection if no valid customer was selected
            const currentCustomerId = form.getValues("customerId" as any);
            if (!currentCustomerId || currentCustomerId === "create-new") {
              form.setValue("customerId" as any, "", {
                shouldValidate: true,
                shouldDirty: true,
              });
            }
            setDialogs((d) => ({ ...d, customerCreate: false }));
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("features.quotes.quoteEditor.index.createNewCustomer")}</DialogTitle>
          </DialogHeader>
          <CustomerForm
            onSubmit={handleCreateCustomer}
            onCancel={() => {
              // Reset form to empty customer selection when canceling
              form.setValue("customerId" as any, "", {
                shouldValidate: true,
                shouldDirty: true,
              });
              setDialogs((d) => ({ ...d, customerCreate: false }));
            }}
            isSubmitting={isCreatingCustomer}
          />
        </DialogContent>
      </Dialog>

      {/* Template Change Confirmation Dialog */}
      <Dialog
        open={dialogs.templateChange}
        onOpenChange={(open) => !open && handleCancelTemplateChange()}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("features.quotes.quoteEditor.index.changeTemplate")}</DialogTitle>
            <DialogDescription>
              {t("features.quotes.quoteEditor.index.changingTo")} {getPendingTemplateName()} {t("features.quotes.quoteEditor.index.willResetYourCurrent")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelTemplateChange}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleConfirmTemplateChange}>{t("features.quotes.quoteEditor.index.continue")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
